import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {MTAApi} from "components/src/api/admin/generated";
import {DialogCloseCause} from "components/src/common/Enums";
import {RenderAlert} from "components/src/common/RenderAlert";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {logger} from "components/src/utils/Logger";
import {Utils} from "components/src/utils/Utils";
import {useEffect, useRef, useState} from 'react'
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import {useNavigate} from "react-router-dom";

/*
 * REST api for accessing the back-end
 */
const mtaApi = new MTAApi(ApiConfig.config());

/**
 * The OnClose event should be handled by the owner. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause : DialogCloseCause) => void;

interface MTAConfigPopupProps {
    onClose : OnClose
}

export const MTAConfigPopup = ({onClose} : MTAConfigPopupProps ) =>
{
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * The current MTA config
     */
    const [currentConfig, setCurrentConfig] = useState<string>("");
    /**
     * The new MTA config
     */
    const [newConfig, setNewConfig] = useState<string>("");
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Set to true while saving to show a spinner on the download button
     */
    const [saving, setSaving] = useState<boolean>(false);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * Reference to the textarea element for focus management
     */
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    /**
     * Load all MTA config
     */
    useEffect(() =>
    {
        logger.debug("Getting MTA config");

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        mtaApi.mTAControllerGetMainConfig()
            .then(response => {
                setCurrentConfig(response.data);
                setNewConfig(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting MTA config", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        setSpinnerStateCounter,
        t
    ]);

    // called when the dialog is closed without clicking export
    const onHideDialog = () => {
        onClose(DialogCloseCause.Cancel);
    }

    const save = () =>
    {
        setSaving(true);

        mtaApi.mTAControllerSetMainConfig(newConfig).then(() =>
        {
            // restart postfix
            mtaApi.mTAControllerRestartPostfix().then(() => {
                onClose(DialogCloseCause.Succes);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error restarting postfix", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
        })
        .catch((err: Error | AxiosError) => {
            logger.error("Error saving MTA config", err);
            Utils.redirectToLoginIfNotAuthenticated(err, navigate);
            setErrorMessage(Utils.getErrorMessage(err, t));
        })
        .finally(() => {
            setSaving(false);
        });
    }

    return (
        <Modal show={true}
               onHide={() => onHideDialog()}
               onEntered={() => textareaRef.current?.focus()}
               size="xl"
               fullscreen="lg-down"
               backdrop="static"
        >
            <Modal.Header closeButton>
                <Modal.Title>{t('mta.config')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>

                <InputGroup className="mb-3">
                    <Form.Control
                        className="font-monospace white-space-pre"
                        as="textarea"
                        ref={textareaRef}
                        onChange={e => setNewConfig(e.target.value)}
                        value={newConfig}
                        rows={20}
                    />
                </InputGroup>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="success"
                        onClick={save}
                        disabled={currentConfig === newConfig}>
                    {saving && <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        aria-hidden="true"
                        className="me-1"
                    />}
                    {saving ? t("common.saving") : t("common.save")}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
